import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { AutocompleteSettings } from '../../models/autocomplete-settings.model';
import { UtilityFunctions } from '../../utilities/functions.models';

@Component({
  standalone: false,
  selector: 'bt-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss']
})
export class MultiselectComponent implements OnChanges {

  @ViewChild('defaultItemTemplate', { static: true }) public defaultItemTemplate: TemplateRef<any>;

  @Input() public datasource: any[];
  @Input() public nextDatasource: any[];
  @Input() public currentSelections: any[] = [];
  @Input() public settings: AutocompleteSettings;
  @Input() public disabled: boolean;
  @Input() public page: number;
  @Input() public loaderId: string;
  @Input() public itemTemplate: TemplateRef<any>;
  @Input() public showOptions: boolean;

  @Output() public selections = new EventEmitter<any[]>();
  @Output() public nextPage = new EventEmitter<any>();
  @Output() public filterChange = new EventEmitter<string>();
  @Output() public open = new EventEmitter<any>();

  public currentTemplate: TemplateRef<any>;
  public valueToView: string;
  public datasourceCopy: any[];
  public searchText: string;
  public inputOnHover: boolean;
  public bottomHalf: boolean;

  private inputTimeout: any;
  private checkTimeout: any;

  @HostListener('window:click')
  public keyUpEvent() {
    if (!this.inputOnHover) this.showOptions = false;
  }

  public ngOnChanges(changes: SimpleChanges) {
    const primitive = this.settings.stringsDs;
    const valueProp = this.settings.valueProperty;
    if (this.settings && (primitive || !!valueProp)) {
      if (changes.datasource) {
        if (this.datasource && this.currentSelections) {
          this.datasource.forEach(el =>
            el.isMultiChecked = this.currentSelections.some(x => (primitive && x === el) || (!primitive && valueProp && x[valueProp] === el[valueProp])));
        }
        this.datasourceCopy = UtilityFunctions.DeepClone(this.datasource);
      }
      if (changes.currentSelections) {
        this.setValueToView();
      }
      if (changes.nextDatasource && this.nextDatasource && this.datasource && this.settings.serverPagination) {
        this.nextDatasource.forEach(el =>
          el.isMultiChecked = this.currentSelections.some(x => (primitive && x === el) || (!primitive && valueProp && x[valueProp] === el[valueProp])));
        this.datasource = this.datasource.concat(this.nextDatasource);
        this.datasourceCopy = UtilityFunctions.DeepClone(this.datasource);
      }
    } else {
      this.valueToView = null;
    }
    if (changes.itemTemplate && this.itemTemplate) {
      this.currentTemplate = this.itemTemplate;
    }
  }

  public ngAfterViewInit() {
    this.currentTemplate = this.itemTemplate || this.defaultItemTemplate;
  }

  public onChangeCheck(item: any) {
    if (!this.currentSelections) this.currentSelections = [];
    const primitive = this.settings.stringsDs;
    const valueProp = this.settings.valueProperty;
    const index = this.currentSelections.findIndex(el => (primitive && item === el) || (!primitive && valueProp && item[valueProp] === el[valueProp]));
    index >= 0 ? this.currentSelections.splice(index, 1) : this.currentSelections.push(item);
    this.setValueToView();
    const elementToUpdate = !primitive && !!valueProp ? this.datasource.find(el => item[valueProp] === el[valueProp]) : null;
    if (elementToUpdate) { elementToUpdate.isMultiChecked = item.isMultiChecked; }
    clearTimeout(this.checkTimeout);
    this.checkTimeout = setTimeout(() => {
      this.selections.emit(this.currentSelections);
    }, 500);
  }

  public onSelect(event: Event, item: any) {
    event.stopPropagation();
    item.isMultiChecked = !item.isMultiChecked;
    this.onChangeCheck(item);
  }

  public onClickInput(ev: any) {
    if (!this.disabled) {
      this.showOptions = !this.showOptions;
      if (this.showOptions) {
        this.open.emit();
        this.bottomHalf = ev.clientY > window.innerHeight / 2;
      }
    }
  }

  public inViewportAction(ev: { isVisible: boolean }, item: any) {
    if (ev.isVisible && this.settings.serverPagination) {
      const primitive = this.settings.stringsDs;
      const valueProp = this.settings.valueProperty;
      const index = this.datasourceCopy.findIndex(el => (primitive && item === el) || (!primitive && valueProp && item[valueProp] === el[valueProp]));
      if (index === this.datasource.length - 1 && this.datasource.length / this.settings.paginationSize === this.page + 1) {
        this.nextPage.emit();
      }
    }
  }

  public onFilterChange() {
    clearTimeout(this.inputTimeout);
    this.inputTimeout = setTimeout(() => {
      this.settings.serverPagination ?
        this.filterChange.emit(this.searchText) :
        this.filterDs();
    }, 500);
  }

  private filterDs() {
    const primitive = this.settings.stringsDs;
    const textProp = this.settings.textProperty;
    const search = this.searchText ? this.searchText.toLowerCase() : '';
    this.datasourceCopy = UtilityFunctions.DeepClone(this.datasource.filter(el => {
      const field: string = primitive ? el : el[textProp];
      return field.toLowerCase().indexOf(search) >= 0;
    }));
  }

  private setValueToView() {
    if (!!this.currentSelections && this.currentSelections.length) {
      const primitive = this.settings.stringsDs;
      const textProp = this.settings.textProperty;
      this.valueToView = primitive ? this.currentSelections[0] : this.currentSelections[0][textProp];
      if (this.currentSelections.length > 1) {
        this.valueToView = this.valueToView + ' (+' + (this.currentSelections.length - 1).toString() + ' other';
        this.valueToView = this.valueToView + (this.currentSelections.length > 2 ? 's)' : ')');
      }
    } else {
      this.valueToView = null;
    }
  }
}
